import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { FsdAvailabilityCollection } from './lib/fsd-availability'
import type { RenderedProof } from './lib/rendered-proof'
import { getPreferredColorScheme, type ColorScheme } from './lib/theme'
import type { SegmentCollection, TakeoverCollection } from './lib/types'

type MapColorExpr = maplibregl.DataDrivenPropertyValueSpecification<string>

const BASEMAP = {
  tileSize: 512,
  attribution: '© OpenStreetMap contributors © CARTO',
  light: {
    tiles: ['https://basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png'],
    paint: { 'raster-opacity': 1, 'raster-saturation': -0.08, 'raster-contrast': 0.04 },
  },
  dark: {
    tiles: ['https://basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}@2x.png'],
    paint: { 'raster-opacity': 1, 'raster-saturation': -0.15, 'raster-contrast': 0.08 },
  },
}

const FSD_MARKET_PAINT: Record<ColorScheme, {
  fill: MapColorExpr
  outline: MapColorExpr
  line: MapColorExpr
  lineOpacity: number
}> = {
  light: {
    fill: [
      'match', ['get', 'status'],
      'active', 'rgba(13, 148, 136, 0.2)',
      'limited', 'rgba(217, 119, 6, 0.16)',
      'rgba(0, 0, 0, 0.05)',
    ],
    outline: [
      'match', ['get', 'status'],
      'active', 'rgba(13, 148, 136, 0.65)',
      'limited', 'rgba(217, 119, 6, 0.55)',
      'rgba(55, 65, 81, 0.45)',
    ],
    line: [
      'match', ['get', 'status'],
      'active', '#0d9488',
      'limited', '#d97706',
      '#4b5563',
    ],
    lineOpacity: 0.85,
  },
  dark: {
    fill: [
      'match', ['get', 'status'],
      'active', 'rgba(94, 234, 212, 0.14)',
      'limited', 'rgba(245, 158, 11, 0.12)',
      'rgba(255, 255, 255, 0.05)',
    ],
    outline: [
      'match', ['get', 'status'],
      'active', 'rgba(94, 234, 212, 0.55)',
      'limited', 'rgba(245, 158, 11, 0.5)',
      'rgba(255, 255, 255, 0.22)',
    ],
    line: [
      'match', ['get', 'status'],
      'active', '#5eead4',
      'limited', '#f59e0b',
      '#6b7280',
    ],
    lineOpacity: 0.75,
  },
}

const TRUST_COLORS: Record<string, string> = {
  high_trust: '#5eead4',
  driven: '#60a5fa',
  undriven: '#9ca3af',
}

export interface MapController {
  map: maplibregl.Map
  whenLayersReady: () => Promise<void>
  waitForIdle: () => Promise<void>
  setSegments: (collection: SegmentCollection) => void
  setTakeover: (collection: TakeoverCollection) => void
  setFsdMarkets: (collection: FsdAvailabilityCollection) => void
  setFsdMarketsVisible: (visible: boolean) => void
  getRenderedProof: () => RenderedProof
  isMapIdle: () => boolean
  fitBounds: (
    bounds: [number, number, number, number],
    options?: { padding?: number; maxZoom?: number; duration?: number },
  ) => void
  onSegmentClick: (cb: (props: SegmentCollection['features'][0]['properties']) => void) => void
  onTakeoverClick: (cb: (props: TakeoverCollection['features'][0]['properties']) => void) => void
  onFsdMarketClick: (cb: (props: FsdAvailabilityCollection['features'][0]['properties']) => void) => void
  setColorScheme: (scheme: ColorScheme) => void
}

export function createMap(
  container: HTMLElement,
  bounds: [number, number, number, number],
  options?: { verifyMode?: boolean },
): MapController {
  const verifyMode = options?.verifyMode ?? new URLSearchParams(location.search).has('verify')
  let colorScheme: ColorScheme = getPreferredColorScheme()
  let layersReady = false
  let pendingSegments: SegmentCollection | null = null
  let pendingTakeover: TakeoverCollection | null = null
  let resolveLayersReady: (() => void) | null = null
  const layersReadyPromise = new Promise<void>((resolve) => {
    resolveLayersReady = resolve
  })

  function basemapVisibility(scheme: ColorScheme): 'visible' | 'none' {
    return scheme === 'light' ? 'visible' : 'none'
  }

  function basemapPaint(scheme: ColorScheme): Record<string, number> {
    const paint: Record<string, number> = { ...BASEMAP[scheme].paint }
    if (verifyMode) paint['raster-opacity'] = 0.88
    return paint
  }

  const map = new maplibregl.Map({
    container,
    style: {
      version: 8,
      sources: {
        'basemap-light': {
          type: 'raster',
          tiles: BASEMAP.light.tiles,
          tileSize: BASEMAP.tileSize,
          attribution: BASEMAP.attribution,
        },
        'basemap-dark': {
          type: 'raster',
          tiles: BASEMAP.dark.tiles,
          tileSize: BASEMAP.tileSize,
          attribution: BASEMAP.attribution,
        },
      },
      layers: [
        {
          id: 'basemap-light',
          type: 'raster',
          source: 'basemap-light',
          layout: { visibility: basemapVisibility(colorScheme) },
          paint: basemapPaint('light'),
        },
        {
          id: 'basemap-dark',
          type: 'raster',
          source: 'basemap-dark',
          layout: { visibility: colorScheme === 'dark' ? 'visible' : 'none' },
          paint: basemapPaint('dark'),
        },
      ],
    },
    center: [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2],
    zoom: 4,
    attributionControl: false,
    canvasContextAttributes: { preserveDrawingBuffer: true },
  })

  if (!verifyMode) {
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
  }

  let segmentHandler: ((props: SegmentCollection['features'][0]['properties']) => void) | null = null
  let takeoverHandler: ((props: TakeoverCollection['features'][0]['properties']) => void) | null = null
  let fsdMarketHandler: ((props: FsdAvailabilityCollection['features'][0]['properties']) => void) | null = null
  let pendingFsdMarkets: FsdAvailabilityCollection | null = null

  function applySegmentData(collection: SegmentCollection) {
    const source = map.getSource('segments') as maplibregl.GeoJSONSource | undefined
    if (!source) return
    source.setData({
      type: 'FeatureCollection',
      features: collection.features.map((f) => ({
        type: 'Feature',
        geometry: f.geometry,
        properties: {
          id: f.properties.id,
          trust: f.properties.trust,
          kind: f.properties.kind,
          sampleCount: f.properties.sampleCount,
          sourceCount: f.properties.sourceCount,
          sources: JSON.stringify(f.properties.sources),
          region: f.properties.region ?? '',
          fsdPercentage: f.properties.fsdPercentage ?? 0,
        },
      })),
    } as GeoJSON.FeatureCollection)
  }

  function applyTakeoverData(collection: TakeoverCollection) {
    const source = map.getSource('takeover') as maplibregl.GeoJSONSource | undefined
    if (!source) return
    source.setData(collection as GeoJSON.FeatureCollection)
  }

  function applyFsdMarketData(collection: FsdAvailabilityCollection) {
    const source = map.getSource('fsd-markets') as maplibregl.GeoJSONSource | undefined
    if (!source) return
    source.setData(collection as GeoJSON.FeatureCollection)
  }

  function applyFsdMarketPaint(scheme: ColorScheme) {
    const paint = FSD_MARKET_PAINT[scheme]
    if (!map.getLayer('fsd-markets-fill')) return
    map.setPaintProperty('fsd-markets-fill', 'fill-color', paint.fill)
    map.setPaintProperty('fsd-markets-fill', 'fill-outline-color', paint.outline)
    map.setPaintProperty('fsd-markets-line', 'line-color', paint.line)
    map.setPaintProperty('fsd-markets-line', 'line-opacity', paint.lineOpacity)
  }

  function applyBasemapTheme(scheme: ColorScheme) {
    if (!map.getLayer('basemap-light') || !map.getLayer('basemap-dark')) return
    map.setLayoutProperty('basemap-light', 'visibility', scheme === 'light' ? 'visible' : 'none')
    map.setLayoutProperty('basemap-dark', 'visibility', scheme === 'dark' ? 'visible' : 'none')
    map.setPaintProperty('basemap-light', 'raster-opacity', basemapPaint('light')['raster-opacity'])
    map.setPaintProperty('basemap-dark', 'raster-opacity', basemapPaint('dark')['raster-opacity'])
  }

  function applyTakeoverStroke(scheme: ColorScheme) {
    if (!map.getLayer('takeover-circles')) return
    map.setPaintProperty('takeover-circles', 'circle-stroke-color', scheme === 'light' ? '#ffffff' : '#0b0f14')
  }

  function applyColorScheme(scheme: ColorScheme) {
    colorScheme = scheme
    applyBasemapTheme(scheme)
    applyFsdMarketPaint(scheme)
    applyTakeoverStroke(scheme)
  }

  map.on('load', () => {
    map.addSource('fsd-markets', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    map.addLayer({
      id: 'fsd-markets-fill',
      type: 'fill',
      source: 'fsd-markets',
      paint: {
        'fill-color': FSD_MARKET_PAINT[colorScheme].fill,
        'fill-outline-color': FSD_MARKET_PAINT[colorScheme].outline,
      },
    })
    map.addLayer({
      id: 'fsd-markets-line',
      type: 'line',
      source: 'fsd-markets',
      paint: {
        'line-color': FSD_MARKET_PAINT[colorScheme].line,
        'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.6, 5, 1.2, 10, 2],
        'line-opacity': FSD_MARKET_PAINT[colorScheme].lineOpacity,
      },
    })

    map.addSource('segments', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    map.addLayer({
      id: 'segments-line',
      type: 'line',
      source: 'segments',
      paint: {
        'line-color': [
          'match', ['get', 'trust'],
          'high_trust', TRUST_COLORS.high_trust,
          'driven', TRUST_COLORS.driven,
          TRUST_COLORS.undriven,
        ],
        'line-width': verifyMode
          ? [
              'match', ['get', 'trust'],
              'undriven', 14,
              'high_trust', 10,
              9,
            ]
          : [
              'interpolate', ['linear'], ['zoom'],
              2, 3,
              4, 5,
              7, 7,
              10, 9,
              14, 12,
            ],
        'line-opacity': [
          'match', ['get', 'trust'],
          'undriven', 0.95,
          0.9,
        ],
      },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    })

    map.addSource('takeover', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    map.addLayer({
      id: 'takeover-circles',
      type: 'circle',
      source: 'takeover',
      paint: {
        'circle-radius': verifyMode
          ? ['interpolate', ['linear'], ['zoom'], 8, 16, 11, 24, 14, 28]
          : ['interpolate', ['linear'], ['zoom'], 4, 6, 10, 11, 14, 15],
        'circle-color': [
          'match', ['get', 'layer'],
          'hazard', '#f59e0b',
          'disengagement', '#f472b6',
          '#94a3b8',
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': colorScheme === 'light' ? '#ffffff' : '#0b0f14',
        'circle-opacity': 0.95,
      },
    })

    layersReady = true
    if (pendingFsdMarkets) applyFsdMarketData(pendingFsdMarkets)
    if (pendingSegments) applySegmentData(pendingSegments)
    if (pendingTakeover) applyTakeoverData(pendingTakeover)
    resolveLayersReady?.()

    map.on('click', 'fsd-markets-fill', (e) => {
      if (!e.features?.[0]?.properties || !fsdMarketHandler) return
      fsdMarketHandler(e.features[0].properties as FsdAvailabilityCollection['features'][0]['properties'])
    })
    map.on('mouseenter', 'fsd-markets-fill', () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', 'fsd-markets-fill', () => { map.getCanvas().style.cursor = '' })

    map.on('click', 'segments-line', (e) => {
      if (!e.features?.[0]?.properties || !segmentHandler) return
      const raw = e.features[0].properties as SegmentCollection['features'][0]['properties'] & { sources?: string }
      segmentHandler({
        ...raw,
        sources: typeof raw.sources === 'string' ? JSON.parse(raw.sources) : raw.sources,
      })
    })

    map.on('click', 'takeover-circles', (e) => {
      if (!e.features?.[0]?.properties || !takeoverHandler) return
      takeoverHandler(e.features[0].properties as TakeoverCollection['features'][0]['properties'])
    })

    map.on('mouseenter', 'segments-line', () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', 'segments-line', () => { map.getCanvas().style.cursor = '' })
    map.on('mouseenter', 'takeover-circles', () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', 'takeover-circles', () => { map.getCanvas().style.cursor = '' })
  })

  return {
    map,
    whenLayersReady: () => layersReadyPromise,
    setSegments(collection) {
      if (!layersReady) {
        pendingSegments = collection
        return
      }
      applySegmentData(collection)
    },
    setTakeover(collection) {
      if (!layersReady) {
        pendingTakeover = collection
        return
      }
      applyTakeoverData(collection)
    },
    setFsdMarkets(collection) {
      if (!layersReady) {
        pendingFsdMarkets = collection
        return
      }
      applyFsdMarketData(collection)
    },
    setFsdMarketsVisible(visible) {
      const v = visible ? 'visible' : 'none'
      if (map.getLayer('fsd-markets-fill')) map.setLayoutProperty('fsd-markets-fill', 'visibility', v)
      if (map.getLayer('fsd-markets-line')) map.setLayoutProperty('fsd-markets-line', 'visibility', v)
    },
    waitForIdle() {
      return new Promise<void>((resolve) => {
        map.once('idle', () => resolve())
        map.triggerRepaint()
      })
    },
    getRenderedProof() {
      const center = map.getCenter()
      const canvas = map.getCanvas()
      const box: [maplibregl.PointLike, maplibregl.PointLike] = [
        [0, 0],
        [canvas.width, canvas.height],
      ]
      const segmentFeats = map.queryRenderedFeatures(box, { layers: ['segments-line'] })
      const takeoverFeats = map.queryRenderedFeatures(box, { layers: ['takeover-circles'] })
      const segIds = new Set(segmentFeats.map((f) => String(f.id ?? f.properties?.id ?? '')))
      const tkIds = new Set(takeoverFeats.map((f) => String(f.id ?? f.properties?.id ?? '')))
      return {
        renderedSegments: segIds.size,
        renderedTakeover: tkIds.size,
        sourceSegments: map.querySourceFeatures('segments').length,
        sourceTakeover: map.querySourceFeatures('takeover').length,
        zoom: map.getZoom(),
        center: [center.lng, center.lat],
      }
    },
    isMapIdle: () => Boolean(map.isStyleLoaded() && !map.isMoving()),
    fitBounds(b, opts) {
      map.fitBounds([[b[0], b[1]], [b[2], b[3]]], {
        padding: opts?.padding ?? 48,
        maxZoom: opts?.maxZoom ?? 10,
        duration: opts?.duration ?? 800,
      })
    },
    onSegmentClick(cb) { segmentHandler = cb },
    onTakeoverClick(cb) { takeoverHandler = cb },
    onFsdMarketClick(cb) { fsdMarketHandler = cb },
    setColorScheme(scheme) {
      applyColorScheme(scheme)
    },
  }
}

export function trustLabel(tier: string): string {
  switch (tier) {
    case 'high_trust': return 'High Trust'
    case 'driven': return 'Driven'
    case 'undriven': return 'Undriven'
    default: return tier
  }
}